import React, { useState, useEffect, useRef } from "react";
import { NavLink } from "react-router-dom";

const Navbar = () => {
  const [menuActive, setMenuActive] = useState(false);
  // For the "scrolled-header" styling, we keep your existing logic:
  const [isScrolled, setIsScrolled] = useState(false);

  // For offset-hiding on mobile:
  const [lastScrollPos, setLastScrollPos] = useState(0);
  const [navOffset, setNavOffset] = useState(0);
  const [navHeight, setNavHeight] = useState(0);

  // We detect screen size to know if it’s mobile (< 768).
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // We'll reference the <header> element to measure its height
  const navRef = useRef(null);

  // Measure the navbar height once it’s mounted
  useEffect(() => {
    if (navRef.current) {
      setNavHeight(navRef.current.offsetHeight);
    }
  }, []);

  // Update `isMobile` on window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Listen for scroll changes
  useEffect(() => {
    const handleScroll = () => {
      const currentPos = window.pageYOffset;
      setIsScrolled(currentPos > 30);

      // Only apply offset-hiding logic on mobile
      if (!isMobile) {
        // On large screens: never offset. Keep it visible at 0
        setNavOffset(0);
      } else {
        let newOffset = navOffset;
        // If user is above 300px, keep the nav fully visible
        if (currentPos < 300) {
          newOffset = 0;
        } else {
          // Past 300px => do the usual hide-on-scroll
          const diff = currentPos - lastScrollPos;
          newOffset += diff;

          // Clamp between 0 and navHeight
          if (newOffset < 0) {
            newOffset = 0;
          } else if (newOffset > navHeight) {
            newOffset = navHeight;
          }
        }
        setNavOffset(newOffset);
      }

      setLastScrollPos(currentPos);
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [lastScrollPos, navOffset, navHeight, isMobile]);

  // Toggle the hamburger menu
  const toggleMenu = () => {
    setMenuActive((prev) => !prev);
  };

  const closeMenu = () => {
    setMenuActive(false);
  };

  // Combine your scrolled-header class usage if you want a different BG or shadow
  const headerClassNames = [
    isScrolled ? "scrolled-header" : ""
  ]
    .join(" ")
    .trim();

  return (
    <header
      ref={navRef}
      className={headerClassNames}
      style={{
        // We simulate the hide/slide using translateY
        transform: `translateY(-${navOffset}px)`,
        transition: "transform 0.2s linear"
      }}
    >
      <nav className="navbar container">
        <img src="/assets/images/favicon/favicon-200.png" alt="BC Vancouver Logo" className="logo-img" onClick={closeMenu}/>
        <NavLink to="/" className="logo" onClick={closeMenu}>
          BC <span>Vanc</span>ouver
        </NavLink>

        <ul className={`nav-links ${menuActive ? "active" : ""}`}>
          <li>
            <NavLink
              to="/"
              className={({ isActive }) => (isActive ? "active" : "")}
              onClick={closeMenu}
            >
              Home
            </NavLink>
          </li>
          <li>
            <NavLink
              to="/articles"
              className={({ isActive }) => (isActive ? "active" : "")}
              onClick={closeMenu}
            >
              Articles
            </NavLink>
          </li>
          <li>
            <NavLink
              to="/trails"
              className={({ isActive }) => (isActive ? "active" : "")}
              onClick={closeMenu}
            >
              Trails
            </NavLink>
          </li>
          <li>
            <NavLink
              to="/contact"
              className={({ isActive }) => (isActive ? "active" : "")}
              onClick={closeMenu}
            >
              Contact
            </NavLink>
          </li>
        </ul>

        {/* Hamburger Menu Toggle */}
        <div
          className={`menu-toggle ${menuActive ? "open" : ""}`}
          onClick={toggleMenu}
          aria-label="Toggle navigation menu"
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter") toggleMenu();
          }}
        >
          <span></span>
          <span></span>
          <span></span>
        </div>
      </nav>
    </header>
  );
};

export default Navbar;